import React from "react";
import { Image } from "../../../components/Image/Image";
import { OrderHistory, OrderHistoryProductImage, ImageType } from "@core/types";
import classnames from "classnames/bind";
import styles from "./order-card.module.scss";
import { useHistory } from "react-router-dom";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { IonIcon } from "@ionic/react";
import { star, starOutline } from "ionicons/icons";
import Truck from "../../../assets/icon/truck.svg";
import { useOrderChatUnreadCount } from "../../../providers/Chat";
import ChatUnreadBadge from "../../../parhato2/src/entities/ChatUnreadBadge";
import { useConfig } from "../../../providers/config";

interface OrderCardProps {
  order: OrderHistory;
}

const cx = classnames.bind(styles);

const OrderCard = ({ order }: OrderCardProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const orderDateCreated = dayjs(order.createdAt).format("DD.MM.YYYY в HH:mm");
  const config = useConfig();
  const isOrderChatEnabled = !!config.isOrderChatEnabled;
  const chatUnreadCount = useOrderChatUnreadCount({ orderId: Number(order.id) || undefined, isOrderChatEnabled });

  const handleClickOrderCard = () => {
    history.push("/my-order-detail/" + order.id);
  };
  const filteredOrderProductImage = order.productImage.filter((productImage) => productImage.images.length);

  const placeholdersCount = 4 - Math.min(filteredOrderProductImage.length, 4);

  const placeholders = Array.from({ length: placeholdersCount }, (_, index) => ({
    images: [
      {
        alt: "",
        id: "",
        previewSrc: "",
        src: "placeholder",
      },
    ],
    productId: index,
  })) as OrderHistoryProductImage[];

  const orderProductImages = filteredOrderProductImage.slice(0, 4).concat(placeholders);

  const isStatusOneOfTheFollowing = [
    "cancelled",
    "failed",
    "processing",
    "pending",
    "out-for-delivery",
    "pending-delivery",
    "completed",
    "new",
  ].includes(order.status);

  const orderTotal = Number(order?.total ?? 0);

  const getProductWord = (n: number) => {
    const d = n % 10;
    const dd = n % 100;
    if (dd >= 11 && dd <= 14) return "товаров";
    if (d === 1) return "товар";
    if (d >= 2 && d <= 4) return "товара";
    return "товаров";
  };

  const orderProductWords = getProductWord(order.itemsCount);

  return (
    <div className="relative flex w-full lg:h-[11rem] rounded-xl p-2 bg-[#f4f4f4]" onClick={handleClickOrderCard}>
      <ChatUnreadBadge count={chatUnreadCount} className="absolute top-1 left-1 z-10" />
      <div className="flex-shrink-0 grid grid-cols-2 w-[6.25rem] h-[6.25rem] lg:h-[10rem] lg:w-[10rem] rounded-lg overflow-hidden">
        {orderProductImages.map(({ productId, images }) => {
          const image = images[0] as ImageType;

          return (
            <div key={productId} className="flex items-center justify-center w-full h-full rounded-2xl overflow-hidden">
              <Image src={image.base64} className="w-full h-full m-0.5" />
            </div>
          );
        })}
      </div>
      <div className="flex-1 items-end mx-2">
        <div className="flex flex-col sm:flex-row gap-1">
          <span className="font-bold text-black">{orderTotal} p</span>
          {order.shippingTotal > 0 && (
            <div className="flex items-center">
              <span className="text-sm flex items-center gap-0.5 text-primary ">
                {"(" + order.shippingTotal + "₽"}
                <IonIcon icon={Truck} className="w-4 h-4" />
                {")"}
              </span>
              {order.isExpressDelivery && <span className="text-sm">⚡</span>}
            </div>
          )}
        </div>
        <p className="text-gray-400 text-sm font-bold">
          {order.itemsCount} {orderProductWords}
        </p>
        {!!order.reviewRating && (
          <div className="flex items-center gap-0.5 mt-1">
            {[...Array(5)].map((_, i) => (
              <IonIcon
                key={i}
                icon={i < order.reviewRating! ? star : starOutline}
                color="warning"
                className="w-4 h-4"
              />
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-col items-end mx-1 gap-1">
        <strong
          className={cx("orderStatusBar", {
            cancel: order.status === "cancelled" || order.status === "failed",
            inProcess:
              !isStatusOneOfTheFollowing ||
              order.status === "processing" ||
              order.status === "pending" ||
              order.status === "out-for-delivery" ||
              order.status === "pending-delivery",
            complete: order.status === "completed",
            new: order.status === "new",
          })}
        >
          {t(order.status) || order.statusName}
        </strong>

        <div className="text-gray-400 text-xs font-semibold">{orderDateCreated}</div>
        <div className="text-gray-400 text-xs font-semibold ">№ {order.id}</div>
      </div>
    </div>
  );
};

export default OrderCard;
